import { useEffect, useState } from 'react';
import MarcaLogo from './MarcaLogo';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import FaixaNovaVersao from '@/components/layout/FaixaNovaVersao';
import FaixaCopiaVelha from '@/components/layout/FaixaCopiaVelha';
import { PAPEIS } from '@/lib/constants';
import { usandoEmuladores } from '@/lib/firebase';
import { CANAIS_CONVERSA } from '@/conversas/canais';
import { temFuncao } from '@/equipe/funcoes';
import { ouvirClinica } from '@/services/clinicasService';
import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { PAPEIS_MENU_PRODUTIVIDADE } from '@/produtividade/disponibilidade';
import { SECAO_REMUNERACAO, salvarSecao, secaoDeveAbrir } from '@/components/layout/secaoMenu';
import {
  IconeAgenda, IconeConfiguracoes, IconeConfirmarAgenda, IconeConversa, IconeDashboard,
  IconeEmAberto, IconeEquipe, IconeFunil, IconeImportacoes, IconeImportar, IconeIndicadores,
  IconeListaDeLeads, IconeMetas, IconeMeuExtrato, IconePainelSaaS, IconePlacar,
  IconeProximosContatos,
  IconePreferencias, IconeRecebimentos, IconeRemuneracao, IconeSecaoRemuneracao,
} from '@/components/layout/iconesMenu';

/**
 * Ícones dos SEGMENTOS — um por espaço de trabalho, não por item.
 *
 * SVG inline porque o projeto não tem biblioteca de ícones, e não vale
 * acrescentar uma dependência inteira por três glifos. Todos com o mesmo
 * tratamento (traço, `currentColor`, 24×24) para parecerem da mesma família.
 *
 * Um ícone por segmento resolve o que as siglas resolviam mal: os três canais
 * de Conversas são o mesmo conceito, e qualquer ícone honesto para eles seria o
 * mesmo balão. Agrupados sob o segmento, a distinção vira o flyout, que mostra
 * o nome por extenso.
 */
const traco = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

/** Vendas/SDR: megafone — captação e prospecção. */
function IconeVendas() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 10v4h3l7 4V6L7 10H4z" {...traco} />
      <path d="M17.5 9.5a3.5 3.5 0 010 5" {...traco} />
      <path d="M7 14v4" {...traco} />
    </svg>
  );
}

/** Recepção: sino de balcão — quem chega e é atendido. */
function IconeRecepcao() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 17h16" {...traco} />
      <path d="M6 15a6 6 0 0112 0" {...traco} />
      <path d="M12 6v3" {...traco} />
      <circle cx="12" cy="5" r="1.4" {...traco} />
    </svg>
  );
}

/** Administração: engrenagem. */
function IconeAdmin() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="3" {...traco} />
      <path
        d="M19.1 13.4a7.6 7.6 0 000-2.8l1.8-1.4-1.8-3.1-2.2.9a7.6 7.6 0 00-2.4-1.4L14.2 3H9.8l-.5 2.6a7.6 7.6 0 00-2.4 1.4l-2.2-.9-1.8 3.1 1.8 1.4a7.6 7.6 0 000 2.8l-1.8 1.4 1.8 3.1 2.2-.9a7.6 7.6 0 002.4 1.4l.5 2.6h4.4l.5-2.6a7.6 7.6 0 002.4-1.4l2.2.9 1.8-3.1-1.8-1.4z"
        {...traco}
      />
    </svg>
  );
}

// Uma entrada de menu por canal do inbox. O rótulo e os papéis saem do mapa dos
// canais, para menu e rota nunca discordarem sobre quem abre o quê.
const itemDeConversa = (id, extras = {}) => ({
  para: `/conversas/${id}`,
  rotulo: CANAIS_CONVERSA[id].rotulo,
  papeis: CANAIS_CONVERSA[id].papeis,
  // ★ A-6: o MESMO balão para os três canais — ver `iconesMenu.jsx`.
  Icone: IconeConversa,
  ...extras,
});

// Navegação por espaço de trabalho (PLANO seção 1: Vendas/SDR × Recepção).
/**
 * O menu. **Exportado** para o teste de alcançabilidade
 * (`web/src/App.rotas.test.js`) conferir de verdade quais rotas têm item aqui,
 * em vez de procurar strings no arquivo.
 */
export const NAVEGACAO = [
  {
    id: 'vendas',
    espaco: 'Vendas / SDR',
    Icone: IconeVendas,
    /**
     * ★ VU (19/set/2026) — UMA lista só, na ordem do dono: os seis itens da
     * EQUIPE primeiro (Conversas — Leads · Próximos contatos · Funil · Agenda ·
     * Lista de leads · Preferências), e depois os do gestor.
     *
     * Dashboard, Metas e vendas, Placar da equipe, Importar Careflow e Meu
     * extrato estão `papeis: ['gestor']` TEMPORARIAMENTE — até as telas de meta
     * e comissão estarem conferidas. REVERTER = voltar cada um para
     * `['gestor', 'sdr']` (docs/vu-testes.md, COMO REVERTER). A guarda de
     * coerência (`vu.guardas.test.js`) exige que a rota acompanhe: item que a
     * SDR não vê é rota em que ela cai em /sem-acesso.
     */
    /**
     * ★★★ A-6 (12/out/2026) — A ORDEM É A DO DONO, e a estrutura ganhou DUAS coisas:
     * um ícone por item e o campo `secao`.
     *
     * ★★★ POR QUE `secao` É CAMPO DO ITEM, e não um nível novo de aninhamento:
     * ⚠⚠ a E4 propôs subseções aninhadas, e mediu o risco: *"a guarda
     * `vu.guardas.test.js` exige que item de menu e rota concordem; mudar a FORMA
     * da estrutura vai mexer nessa guarda e nos 17 testes do AppShell"*.
     * ★★ Conferido: `vu.guardas.test.js:219` e `App.rotas.test.js:31` fazem
     * `NAVEGACAO.flatMap((g) => g.itens)` — um nível novo faria as duas passarem a
     * enxergar OBJETOS DE SEÇÃO em vez de itens, calando as duas guardas de uma vez.
     * ★★★ Com `secao` no item, a lista continua PLANA: as duas guardas seguem
     * **sem nenhuma alteração de travessia**, e quem agrupa é só o desenho.
     *
     * ⚠ Itens de uma mesma seção têm de ficar JUNTOS e na ordem — é o que o render
     * usa para montar o bloco. Há teste afirmando isso.
     *
     * ★ PERMISSÕES: nenhuma mudou. Cada `papeis`/`funcoes` é o de antes, linha por
     * linha — o que mudou foi a ORDEM e o ÍCONE.
     */
    itens: [
      /**
       * ★ A-6: o dono pôs o Dashboard em primeiro.
       * ★★★ A-7c (13/out/2026) — `papeis` voltou a `['gestor', 'sdr']`: é a reversão da VU
       * **só desta linha**. ⚠ O par rota⇔item é obrigatório (`vu.guardas.test.js`): item que a
       * SDR vê sem a rota aberta é um link para `/sem-acesso`; rota aberta sem item é uma tela
       * sem caminho. ★ O negociador entra por `'sdr'` (papel), sem `funcoes`.
       */
      { para: '/dashboard', rotulo: 'Dashboard', papeis: ['gestor', 'sdr'], Icone: IconeDashboard },
      itemDeConversa('resgate'),
      { para: '/proximos-contatos', rotulo: 'Próximos contatos', papeis: ['gestor', 'sdr'], Icone: IconeProximosContatos },
      { para: '/funil', rotulo: 'Funil', papeis: ['gestor', 'sdr'], Icone: IconeFunil },
      { para: '/agenda', rotulo: 'Agenda', papeis: ['gestor', 'sdr'], Icone: IconeAgenda },
      // VU: "Leads" virou "Lista de leads" — só o rótulo; a rota é a mesma.
      { para: '/leads', rotulo: 'Lista de leads', papeis: ['gestor', 'sdr'], Icone: IconeListaDeLeads },
      // ★★ WO lote 2b — a rota é `/indicadores`; `/produtividade` redireciona.
      // ⚠ Os papéis vêm de `PAPEIS_MENU_PRODUTIVIDADE`, a MESMA bandeira que decide a
      // rota em `App.jsx` — fixar `['gestor']` aqui quebraria o par menu⇔rota.
      { para: '/indicadores', rotulo: 'Indicadores de Performance', papeis: PAPEIS_MENU_PRODUTIVIDADE, Icone: IconeIndicadores },
      /**
       * ★★★ A-7a — "Metas e vendas" e "Placar da equipe": OCULTOS DO MENU, rotas intactas.
       *
       * ⚠ As duas telas foram substituídas pela **grade diária no fim do Dashboard** (metas
       * gerais e o mês contra a meta) e pelo botão **"Metas individuais"** em Indicadores.
       * ★★★ **Os itens NÃO foram apagados**, e é o mesmo motivo de "Conversas — API Oficial"
       * logo abaixo: eles continuam em `NAVEGACAO`, com os mesmos `papeis`, para as duas
       * guardas seguirem valendo — `App.rotas.test.js` exige item para toda rota marcada
       * "menu", e `vu.guardas.test.js` exige item⇔rota por papel.
       * ★ `oculto: true` é lido SÓ pelo desenho. Religar = apagar a marca, e nada mais.
       */
      { para: '/metas', rotulo: 'Metas e vendas', papeis: ['gestor'], Icone: IconeMetas, oculto: true },
      { para: '/placar', rotulo: 'Placar da equipe', papeis: ['gestor'], Icone: IconePlacar, oculto: true },
      // ★★ WJ — O ÚNICO ITEM DO MENU QUE PEDE FUNÇÃO, e não só acesso: o extrato é de
      // quem exerce a função de SDR; o negociador não deve nem VER a seção (pedido do
      // dono). `papeis` continua valendo — `funcoes` só ESTREITA, nunca alarga.
      { para: '/meu-extrato', rotulo: 'Meu extrato', papeis: ['gestor', 'sdr'], funcoes: ['sdr'], Icone: IconeMeuExtrato },

      // ---- seção que abre e fecha: Remuneração (A-6) ----
      // ⚠ Os cinco são `['gestor']`, como já eram. Para a SDR a seção fica SEM item
      // visível, e o render não a desenha — é o mesmo princípio do grupo vazio.
      { para: '/recebimentos', rotulo: 'Recebimentos', papeis: ['gestor'], secao: SECAO_REMUNERACAO, Icone: IconeRecebimentos },
      // ★ WG: a pergunta "quem ainda me deve" saiu de /recebimentos e ganhou rota.
      { para: '/recebimentos/em-aberto', rotulo: 'Em aberto', papeis: ['gestor'], secao: SECAO_REMUNERACAO, Icone: IconeEmAberto },
      // VX: `{sistema}` vira o nome do sistema de gestão da clínica (config) na hora de desenhar.
      { para: '/recebimentos/importar', rotulo: 'Importar do {sistema}', papeis: ['gestor'], secao: SECAO_REMUNERACAO, Icone: IconeImportar }, // ★ VU temporário
      { para: '/recebimentos/importacoes', rotulo: 'Importações', papeis: ['gestor'], secao: SECAO_REMUNERACAO, Icone: IconeImportacoes },
      // VC: a folha e as taxas são do gestor. (O extrato de cada um está acima, no
      // bloco da equipe, desde a WG — a SDR já vê o dela.)
      { para: '/remuneracao', rotulo: 'Remuneração', papeis: ['gestor'], secao: SECAO_REMUNERACAO, Icone: IconeRemuneracao },

      /**
       * ★★★ "Conversas — API Oficial": OCULTA DO MENU, rota intacta (A-6).
       *
       * ⚠⚠ O número oficial ainda não está conectado, e a caixa está vazia — o dono
       * pediu para tirá-la da vista. ★★★ **O item NÃO foi apagado**, e isso é o ponto:
       * ele continua em `NAVEGACAO`, com os mesmos `papeis`, para as duas guardas
       * continuarem valendo —
       *   `App.rotas.test.js:31` monta `DO_MENU` a partir daqui e exige que toda rota
       *   marcada "menu" tenha item (`/conversas/leads` é uma delas);
       *   `vu.guardas.test.js` exige item⇔rota por papel.
       * ⚠ Apagar o item faria `/conversas/leads` virar rota órfã e as duas guardas
       * cairiam — por um motivo que não é defeito.
       *
       * ★ `oculto: true` é lido SÓ pelo desenho (o filtro do render). Religar = apagar
       * esta linha, e nada mais.
       */
      itemDeConversa('leads', { oculto: true }),

      // ★ "Preferências é o último item por convenção em toda a navegação" — está
      // escrito duas vezes neste arquivo e afirmado pelo teste de ordem da VU.
      { para: '/vendas/preferencias', rotulo: 'Preferências', papeis: ['gestor', 'sdr'], Icone: IconePreferencias },
    ],
  },
  {
    id: 'recepcao',
    espaco: 'Recepção',
    Icone: IconeRecepcao,
    itens: [
      /* Pacientes saiu do menu enquanto a tela for o placeholder do `EmBreve`:
         a equipe já usa o sistema de verdade, e um item que abre "em breve"
         gasta a confiança de quem clica. A ROTA e a PÁGINA continuam existindo
         — devolver a linha abaixo é tudo o que a reativação exige.
         { para: '/pacientes', rotulo: 'Pacientes', papeis: ['gestor', 'recepcao'] }, */
      // VF-2: era "Agenda do dia". Com as visões de semana e mês o rótulo
      // ficou errado — a tela deixou de ser só do dia.
      { para: '/agenda', rotulo: 'Agenda', papeis: ['recepcao'], Icone: IconeAgenda },
      { para: '/confirmar-agenda', rotulo: 'Confirmar agenda', papeis: ['gestor', 'recepcao'], Icone: IconeConfirmarAgenda },
      itemDeConversa('recepcao'),
      { para: '/recepcao/preferencias', rotulo: 'Preferências', papeis: ['gestor', 'recepcao'], Icone: IconePreferencias },
    ],
  },
  {
    id: 'admin',
    espaco: 'Administração',
    Icone: IconeAdmin,
    itens: [
      { para: '/configuracoes', rotulo: 'Configurações', papeis: ['gestor'], Icone: IconeConfiguracoes },
      { para: '/equipe', rotulo: 'Equipe', papeis: ['gestor'], Icone: IconeEquipe },
      // ⚠ Painel SaaS NÃO está no desenho (que é o menu do GESTOR): ícone de GRADE,
      // escolhido por mim. Ver `iconesMenu.jsx` e `docs/a6-testes.md`.
      { para: '/admin', rotulo: 'Painel SaaS', papeis: ['superadmin'], Icone: IconePainelSaaS },
    ],
  },
];

/** Chevron do accordion: para baixo quando aberto, para a direita quando fechado. */
function Chevron({ aberto }) {
  return (
    <svg
      className={aberto ? 'nav-chevron aberto' : 'nav-chevron'}
      width="12"
      height="12"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      {/* Um só desenho (apontando para a direita); o giro fica no CSS, para a
          transição existir. Trocar o `d` não animaria. */}
      <path
        d="M6 3l5 5-5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Seta do botão de recolher: aponta para onde o menu vai. */
function IconeMenu({ recolhida }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d={recolhida ? 'M6 3l5 5-5 5' : 'M10 3L5 8l5 5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** VX — o rótulo do menu pode citar o sistema de gestão da clínica; o nome vem da config. */
export const rotuloDoItem = (item, config = null) => item.rotulo.replace('{sistema}', nomeDoSistema(config));

/**
 * ★ A-6 — ícone do CABEÇALHO de cada seção. Mapa por nome, para `NAVEGACAO` não
 * precisar repetir o ícone em cinco itens só para a seção tê-lo.
 */
const ICONES_DE_SECAO = { [SECAO_REMUNERACAO]: IconeSecaoRemuneracao };

/**
 * ★★★ A-6 — UM COMPONENTE para o link do menu, usado nos TRÊS lugares (item
 * solto, item de seção e item do flyout do menu recolhido).
 *
 * ⚠⚠ Antes o `<NavLink>` estava escrito duas vezes, e o ícone teria de entrar nas
 * duas — com a chance de ficar em uma só. ★ Um componente é um lugar.
 *
 * ★ O ícone é `aria-hidden` (ver `iconesMenu.jsx`): o rótulo ao lado já diz o que
 * é, e anunciar os dois repetiria sem informar.
 */
function ItemDoMenu({ item, config, naSecao = false, papelDeMenu = false }) {
  const { Icone } = item;
  return (
    <NavLink
      to={item.para}
      end={item.para === '/'}
      {...(papelDeMenu ? { role: 'menuitem' } : {})}
      className={({ isActive }) => {
        const base = naSecao ? 'nav-item nav-item-da-secao' : 'nav-item';
        return isActive ? `${base} ativo` : base;
      }}
    >
      {Icone && <Icone />}
      <span>{rotuloDoItem(item, config)}</span>
    </NavLink>
  );
}

export default function AppShell() {
  const { perfil, usuario, papel, sair, clinicaId, avisoPerfil } = useAuth();
  // VX: só para os rótulos que citam o sistema de gestão da clínica (leitura; sem escrita).
  const [configClinica, setConfigClinica] = useState(null);
  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(clinicaId, (c) => setConfigClinica(c?.config ?? null), () => setConfigClinica(null));
  }, [clinicaId]);
  // Só estado de sessão, sem persistir: recolher é uma preferência do momento
  // (ganhar largura para o inbox), não uma configuração da pessoa.
  const [recolhida, setRecolhida] = useState(false);
  const { pathname } = useLocation();

  const nome = perfil?.nome ?? usuario?.email ?? '';
  /**
   * ★★ WJ — `funcoes` no item é OPCIONAL, e é isso que protege os outros itens.
   *
   * A cláusula curto-circuita em `!i.funcoes`: para os 22 itens que não declaram
   * a chave, a expressão vira `i.papeis.includes(papel) && true` — literalmente
   * o filtro anterior. Dar `funcoes` a todos seria revisar 23 linhas e criar 22
   * chances de errar. (Mutação M2: trocar `!i.funcoes` por `false` faz a cláusula
   * valer para todos e derruba o teste que compara a lista inteira.)
   *
   * ★ O `papel` vem do CLAIM, não de `perfil.papel`: `funcoesDe` deriva do papel
   * quando `funcoes` está ausente — que é o caso da Jéssica e da Talita —, e a
   * autoridade sobre acesso é o claim, a mesma fonte que rules e functions usam.
   * Se um dia o campo do documento divergir do claim, o menu segue o claim.
   * (Mutação M4.)
   *
   * ⚠ `perfil` é `null` enquanto o snapshot não chega. Para `papel: 'sdr'` a
   * derivação já devolve `['sdr']`, então não há piscada para a SDR; para quem é
   * só negociador o item aparece por um instante e some. Aceito: o item não é
   * clicável a tempo, e a página tem o próprio portão.
   */
  const euNaEquipe = { ...perfil, papel };
  /**
   * ★★★ A-6 — `!i.oculto` entra NO FILTRO DO DESENHO, não em `NAVEGACAO`.
   *
   * ⚠⚠ "Conversas — API Oficial" sai da vista sem sair da estrutura: o item
   * continua em `NAVEGACAO` (com os mesmos papéis) para `App.rotas.test.js` e
   * `vu.guardas.test.js` seguirem valendo, e desaparece aqui, onde se decide o
   * que a pessoa vê. ★ Religar é apagar o `oculto: true` de uma linha.
   */
  const grupos = NAVEGACAO.map((g) => ({
    ...g,
    itens: g.itens.filter(
      (i) => !i.oculto
        && i.papeis.includes(papel)
        && (!i.funcoes || i.funcoes.some((f) => temFuncao(euNaEquipe, f))),
    ),
  })).filter((g) => g.itens.length > 0);

  // Segmento do item aberto agora, para o ícone dele ficar aceso quando o menu
  // está recolhido — senão não há como saber onde se está.
  const naRota = (para) => pathname === para || pathname.startsWith(`${para}/`);
  const segmentoAtivo = (grupo) => grupo.itens.some((i) => naRota(i.para));

  /**
   * Accordion dos grupos: começa com APENAS o grupo da rota atual aberto.
   *
   * O menu cresceu para quinze itens em três grupos, e o gestor via todos de
   * uma vez — a lista passava da altura da tela. Abrir só onde a pessoa está
   * mostra o contexto dela e esconde o resto.
   *
   * Estado de sessão, sem persistir, pelo mesmo motivo do `recolhida`: é uma
   * preferência do momento, não uma configuração da pessoa.
   */
  const [abertos, setAbertos] = useState(() => {
    const doInicio = NAVEGACAO.find((g) => g.itens.some((i) => naRota(i.para)));
    return new Set(doInicio ? [doInicio.id] : []);
  });

  /**
   * Navegar para outro grupo abre aquele grupo — sem FECHAR os que a pessoa
   * abriu à mão. Um accordion que fecha o que o usuário abriu desfaz a escolha
   * dele a cada clique no menu.
   */
  useEffect(() => {
    const daRota = grupos.find(segmentoAtivo);
    if (!daRota) return;
    setAbertos((atuais) => (atuais.has(daRota.id) ? atuais : new Set([...atuais, daRota.id])));
  }, [pathname]);

  const alternarGrupo = (id) =>
    setAbertos((atuais) => {
      const proximo = new Set(atuais);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  /**
   * ★★★ A-6 — AS SEÇÕES QUE ABREM E FECHAM (hoje só "Remuneração").
   *
   * ★★ A regra do dono tem duas metades, e a PRECEDÊNCIA é o que importa:
   *   1. a seção abre sozinha quando a página atual está DENTRO dela;
   *   2. senão, lembra a última escolha (`localStorage`, com o uid na chave).
   *
   * ⚠⚠ Se a lembrança viesse primeiro, quem fechou a seção e depois abrisse
   * `/recebimentos` pelo histórico do navegador cairia numa tela cujo item está
   * escondido — sem pista de onde está. ★ A rota vence a lembrança, sempre.
   * ★ A precedência inteira é testável sem navegador: `secaoDeveAbrir`.
   *
   * ⚠ Diferente do accordion dos GRUPOS (que é estado de sessão, de propósito),
   * esta escolha PERSISTE: o gestor que não usa Remuneração a fecha uma vez.
   */
  const [secoesAbertas, setSecoesAbertas] = useState({});
  const secaoDaRota = (itens, secao) => itens.some((i) => i.secao === secao && naRota(i.para));
  /**
   * ⚠⚠ A PRECEDÊNCIA MORA NA FUNÇÃO PURA — e isto é conserto de um defeito que a
   * MUTAÇÃO M44 revelou (12/out): a primeira versão daqui escrevia
   * `if (secaoDaRota(...)) return true;` e **só depois** chamava
   * `secaoDeveAbrir(uid, secao, false)` — com `false` CRAVADO.
   * ★★★ Ou seja: o ramo `if (naSecao) return true` da função pura era CÓDIGO MORTO,
   * e apagá-lo não derrubava teste nenhum. ⚠ A regra passava a existir em dois
   * lugares, e o lugar testado não era o que rodava.
   * ★ Agora o `naSecao` de verdade desce para a função, e ela decide sozinha.
   * ⚠ O estado de sessão continua ANTES da lembrança e DEPOIS da rota — por isso
   * ele só é consultado quando a página está fora da seção.
   */
  const secaoAberta = (itens, secao) => {
    const naSecao = secaoDaRota(itens, secao);
    if (!naSecao && secao in secoesAbertas) return secoesAbertas[secao];
    return secaoDeveAbrir(usuario?.uid, secao, naSecao);
  };
  const alternarSecao = (itens, secao) => {
    const proximo = !secaoAberta(itens, secao);
    setSecoesAbertas((atuais) => ({ ...atuais, [secao]: proximo }));
    salvarSecao(usuario?.uid, secao, proximo);
  };

  /**
   * ★★★ Os itens do grupo em BLOCOS, para o desenho: item solto ou seção.
   *
   * ★ Itens consecutivos com o mesmo `secao` entram num bloco só. ⚠ "Consecutivos"
   * é o contrato — `NAVEGACAO` mantém os cinco juntos, e há teste afirmando isso.
   * Espalhá-los produziria duas seções com o mesmo nome, e o teste cai.
   */
  const emBlocos = (itens) => {
    const blocos = [];
    for (const item of itens) {
      const ultimo = blocos.at(-1);
      if (!item.secao) { blocos.push({ tipo: 'item', item }); continue; }
      if (ultimo?.tipo === 'secao' && ultimo.secao === item.secao) { ultimo.itens.push(item); continue; }
      blocos.push({ tipo: 'secao', secao: item.secao, itens: [item] });
    }
    return blocos;
  };

  return (
    <div className={recolhida ? 'shell nav-recolhida' : 'shell'}>
      <aside className="shell-nav">
        <div className="nav-topo">
          <div className="marca">
            <MarcaLogo />
            <span className="marca-nome">OdontoSDR</span>
          </div>
          {/* Sempre visível: recolhido, é a única saída de volta. */}
          <button
            type="button"
            className="btn-recolher"
            onClick={() => setRecolhida((r) => !r)}
            title={recolhida ? 'Expandir o menu' : 'Recolher o menu'}
            aria-label={recolhida ? 'Expandir o menu' : 'Recolher o menu'}
            aria-expanded={!recolhida}
          >
            <IconeMenu recolhida={recolhida} />
          </button>
        </div>

        <nav>
          {grupos.map((grupo) => {
            const { Icone } = grupo;

            // ---- recolhido: um ícone por segmento, com flyout no hover ----
            if (recolhida) {
              return (
                <div key={grupo.id} className="nav-segmento">
                  <button
                    type="button"
                    className={
                      segmentoAtivo(grupo) ? 'nav-segmento-icone ativo' : 'nav-segmento-icone'
                    }
                    title={grupo.espaco}
                    aria-label={grupo.espaco}
                  >
                    <Icone />
                  </button>

                  {/* Abre no hover e também no foco pelo teclado (`:focus-within`
                      no CSS) — sem isso o menu recolhido ficaria inalcançável
                      para quem navega por Tab. */}
                  <div className="nav-flyout" role="menu" aria-label={grupo.espaco}>
                    <p className="nav-flyout-titulo">{grupo.espaco}</p>
                    {/* ★ A-6: o flyout do menu recolhido mostra a lista PLANA, com os
                        ícones. ⚠ Sem accordion aqui de propósito: o flyout já é um
                        gesto de passagem, e um segundo clique dentro dele para abrir
                        uma seção faria o mouse sair e fechá-lo. */}
                    {grupo.itens.map((item) => (
                      <ItemDoMenu key={item.para} item={item} config={configClinica} papelDeMenu />
                    ))}
                  </div>
                </div>
              );
            }

            // ---- expandido: accordion, com os rótulos por extenso ----
            const aberto = abertos.has(grupo.id);
            return (
              <div key={grupo.id} className="nav-grupo">
                <button
                  type="button"
                  className="nav-grupo-titulo"
                  onClick={() => alternarGrupo(grupo.id)}
                  aria-expanded={aberto}
                >
                  <span>{grupo.espaco}</span>
                  <Chevron aberto={aberto} />
                </button>

                {/* Grupo fechado NÃO renderiza os itens: escondê-los por CSS
                    deixaria os links alcançáveis por Tab, levando o foco para
                    algo invisível. */}
                {aberto &&
                  emBlocos(grupo.itens).map((bloco) => {
                    // ---- item solto ----
                    if (bloco.tipo === 'item') {
                      return <ItemDoMenu key={bloco.item.para} item={bloco.item} config={configClinica} />;
                    }

                    /**
                     * ---- seção que abre e fecha (A-6) ----
                     * ★ Mesma disciplina do grupo: fechada NÃO renderiza os links,
                     * para o Tab não cair em algo invisível.
                     */
                    const IconeSecao = ICONES_DE_SECAO[bloco.secao];
                    const abertaAgora = secaoAberta(grupo.itens, bloco.secao);
                    return (
                      <div key={bloco.secao} className="nav-secao">
                        <button
                          type="button"
                          className={
                            secaoDaRota(grupo.itens, bloco.secao) ? 'nav-secao-titulo ativo' : 'nav-secao-titulo'
                          }
                          onClick={() => alternarSecao(grupo.itens, bloco.secao)}
                          aria-expanded={abertaAgora}
                        >
                          <span className="nav-secao-rotulo">
                            {IconeSecao && <IconeSecao />}
                            {bloco.secao}
                            {/* ★ A contagem é do desenho: diz o que está escondido quando
                                a seção está fechada, sem precisar abri-la. */}
                            <span className="nav-secao-contagem">{bloco.itens.length}</span>
                          </span>
                          <Chevron aberto={abertaAgora} />
                        </button>
                        {abertaAgora &&
                          bloco.itens.map((item) => (
                            <ItemDoMenu key={item.para} item={item} config={configClinica} naSecao />
                          ))}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </nav>

        <div className="nav-rodape">
          {!recolhida && (
            <div className="usuario">
              <strong>{nome}</strong>
              <span>{PAPEIS[papel]?.label ?? papel}</span>
            </div>
          )}
          <button
            type="button"
            className="btn-texto"
            onClick={sair}
            title={recolhida ? `Sair (${nome})` : undefined}
          >
            {recolhida ? '⏻' : 'Sair'}
          </button>
        </div>
      </aside>

      <main className="shell-conteudo">
        {/* ★★ WT 1b — o PERFIL não carregou. ⚠ Sem ele a pessoa aparece sem nome e sem
            função, e as telas que dependem de `funcoes` decidem errado em silêncio.
            ★ Faixa no topo porque atinge o app todo, não uma tela — e FORA do bloco do
            emulador, que é condicional e só existe em desenvolvimento. */}
        {/* ⚠ O nome da classe NÃO pode conter `faixa-aviso`: a guarda da WO proíbe esse
            padrão no AppShell, para a faixa "a seção está em ajuste" não voltar (decisão do
            dono). ★ A guarda está certa — quem muda de nome é esta faixa. */}
        {/* ★★★ WU/U2 — "nova versão disponível". ⚠ AVISA; nunca recarrega sozinho fora do login.
            ★ Fica acima do aviso de perfil porque é do app todo, e antes do `<Outlet />`. */}
        <FaixaNovaVersao />
        {/* ★★★ WU/U3 — a cópia local: outra aba travou a limpeza, ou a equipe chegou incoerente. */}
        <FaixaCopiaVelha />
        {avisoPerfil && (
          <div className="faixa-perfil-ausente" role="alert">
            Não foi possível carregar seu perfil. Recarregue.
          </div>
        )}
        {usandoEmuladores && (
          <div className="faixa-emulador">
            Ambiente local — emuladores do Firebase. Nada aqui toca o projeto odontosdr-app.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
